import { Link } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDate } from "../../../utils/date";
import { formatNumber } from "../../../utils";
import { DeductionDay, DeductionRouteDetail } from "../../../types/deduction.type";

interface SaleDetailModalProps {
  open: boolean;
  onClose: () => void;
  day: DeductionDay | null;
  route: DeductionRouteDetail;
}

interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

const InfoRow = ({ label, children }: InfoRowProps) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
      borderBottom: (theme) => `1px solid ${theme.palette.background.default}`,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box>{children}</Box>
  </Box>
);

export default function SaleDetailModal({
  open,
  onClose,
  day,
  route,
}: SaleDetailModalProps) {
  if (!day) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={500}>
          รายงานการขายแบบละเอียด
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatDate(day.date)} · สาย {route.route_code} · {route.branch_name}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <InfoRow label="สายส่ง">
          <Typography variant="body2" fontWeight={500}>
            {route.route_code}
          </Typography>
        </InfoRow>
        <InfoRow label="สาขา">
          <Typography variant="body2" fontWeight={500}>
            {route.branch_name}
          </Typography>
        </InfoRow>
        <InfoRow label="จำนวนพนักงานในสาย">
          <Typography variant="body2" fontWeight={500}>
            {day.emp_count} คน
          </Typography>
        </InfoRow>
        <InfoRow label="ยอดหักรวมทั้งสายวันนี้">
          <Typography color="error" fontWeight={600}>
            -{formatNumber(day.total_amount)} บาท
          </Typography>
        </InfoRow>

        {day.price_groups.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              รายการที่ทำให้เกิดยอดหัก
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ราคา/หน่วย</TableCell>
                  <TableCell align="right">จำนวนสุทธิ</TableCell>
                  <TableCell align="right">ยอดหัก</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {day.price_groups.map((group, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatNumber(group.price)}</TableCell>
                    <TableCell align="right">
                      {formatNumber(group.net_qty)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error">
                        -{formatNumber(group.deduction_amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          ปิด
        </Button>
        <Button
          component={Link}
          to={`/report/sale/more-details/${day.sale_report_uuid}`}
          variant="contained"
          disabled={!day.sale_report_uuid}
          startIcon={<VisibilityIcon />}
        >
          ดูรายงานการขาย
        </Button>
      </DialogActions>
    </Dialog>
  );
}
